import React, { ChangeEvent, useRef, useState, useEffect } from 'react'
import { Modal } from 'react-responsive-modal'
import { useDispatch, useSelector } from 'react-redux'
import DatePicker from 'react-datepicker'
import { InitialState } from '../../containers/SignInPage/types'
import {
  AddressSuggestions,
  DaDataAddressSuggestion,
  DaDataPartySuggestion,
  PartySuggestions,
} from 'react-dadata'
import autosize from 'autosize'
import $ from 'jquery'
import { checkProfileRole, dadataDots } from '../../utils/helpers'
import moment from 'moment'
import * as profileActions from '../../containers/SignInPage/actions'
import usePrevious from '../../hooks/usePrevious'

// Styles
import './styles.scss'
import 'react-responsive-modal/styles.css'

// Components
import Loading from '../Loading'
import ModalChangeRole from '../ModalChangeRole'
import notification from '../Notification'

export default ({
  modal,
  onClose,
}: {
  modal: boolean
  onClose: () => void
}) => {
  const initialModalChangeRole: {
    modal: boolean
    type: 'teacher' | 'student'
  } = {
    modal: false,
    type: 'teacher',
  }

  const [modalChangeRole, setModalChangeRole] = useState<
    typeof initialModalChangeRole
  >(initialModalChangeRole)
  const [name, setName] = useState<string>('')
  const [patronymicName, setPatronymicName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [openSex, setOpenSex] = useState<boolean>(false)
  const [sex, setSex] = useState<string>('')
  const [isOpenCalendar, setIsOpenCalendar] = useState<boolean>(false)
  const [birthday, setBirthday] = useState<Date | null>(null)
  const [edu, setEdu] = useState<DaDataPartySuggestion | undefined>()
  const [eduHasAlready, setEduHasAlready] = useState<boolean>(false)
  const [group, setGroup] = useState<string>('')
  const [region, setRegion] = useState<DaDataAddressSuggestion | undefined>()
  const [regionHasAlready, setRegionHasAlready] = useState<boolean>(false)
  const [location, setLocation] = useState<
    DaDataAddressSuggestion | undefined
  >()
  const [locationHasAlready, setLocationHasAlready] = useState<boolean>(false)
  const [isValidate, setIsValidate] = useState<boolean>(false)
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [birthdayError, setBirthdayError] = useState<boolean>(false)

  const dispatch = useDispatch()

  const { profile, isProcessChangeProfileRole, isProcessSave } = useSelector(
    (state: { profile: InitialState }) => state.profile
  )

  const { REACT_APP_DADATA_KEY } = process.env

  const isStudent = checkProfileRole(profile?.roles, 'student')
  const isTeacher = checkProfileRole(profile?.roles, 'teacher')

  const prevChangeProfileRole = usePrevious(isProcessChangeProfileRole)

  const suggestionsRef = useRef<PartySuggestions>(null)
  const suggestionsRegionRef = useRef<AddressSuggestions>(null)
  const suggestionsLocationRef = useRef<AddressSuggestions>(null)

  useEffect(() => {
    if (prevChangeProfileRole === true && !isProcessChangeProfileRole) {
      dispatch(profileActions.fetchProfile())
      setModalChangeRole(initialModalChangeRole)
    }
  }, [isProcessChangeProfileRole])

  useEffect(() => {
    if (modal) {
      setTimeout(() => {
        const textareaElement = $('.react-dadata__input')
        autosize(textareaElement)
        $('.questionary').on('DOMNodeInserted', function (e) {
          if ($(e.target).attr('class') === 'react-dadata__suggestions-outer') {
            $(e.target).parent().find('div:first-child').addClass('active')
          }
        })
        $('.questionary').on('DOMNodeRemoved', function (e) {
          if ($(e.target).attr('class') === 'react-dadata__suggestions-outer') {
            $(e.target).parent().find('div:first-child').removeClass('active')
          }
        })
      })
    }
  }, [modal])

  useEffect(() => {
    if (profile && !isLoaded) {
      // Personal
      const parsePatronymic = profile.external_profile.patronymic
      const parseName = profile.external_profile.name
      const parseLastName = profile.external_profile.surname
      const parseSex = profile.external_profile.gender

      setLastName(parseLastName || '')
      setName(parseName || '')
      setPatronymicName(parsePatronymic || '')
      setSex(parseSex || '')
      if (profile.external_profile.birthdate) {
        const parseBithday = moment(profile.external_profile.birthdate).toDate()
        setBirthday(parseBithday)
      }

      // Edu
      if (profile.profile.school && suggestionsRef.current) {
        setEduHasAlready(true)
        suggestionsRef.current.setInputValue(profile.profile.school.value)
        setTimeout(() => autosize.update($('.react-dadata__input')))
      }
      setGroup(profile.profile.school_group || '')

      // Location
      if (
        profile.external_profile.address?.location &&
        suggestionsLocationRef.current
      ) {
        setLocationHasAlready(true)
        suggestionsLocationRef.current.setInputValue(
          profile.external_profile.address.location
        )
      }
      if (
        profile.external_profile.address?.region &&
        suggestionsRegionRef.current
      ) {
        setRegionHasAlready(true)
        suggestionsRegionRef.current.setInputValue(
          profile.external_profile.address.region
        )
      }
      setIsLoaded(true)
    }
  }, [profile])

  const onChangeLastName = (event: ChangeEvent<HTMLInputElement>) => {
    setLastName(event.currentTarget.value)
  }

  const onChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.currentTarget.value)
  }

  const onChangePatronymic = (event: ChangeEvent<HTMLInputElement>) => {
    setPatronymicName(event.currentTarget.value)
  }

  const onChangeGroup = (event: ChangeEvent<HTMLInputElement>) => {
    setGroup(event.currentTarget.value)
  }

  const saveProfile = () => {
    const eduCity =
      edu?.data?.address?.data?.settlement_with_type ||
      edu?.data?.address?.data?.city_with_type

    if (
      (eduCity || eduHasAlready) &&
      group &&
      name &&
      lastName &&
      sex &&
      birthday &&
      moment(birthday) < moment() &&
      (region || regionHasAlready) &&
      (location || locationHasAlready) &&
      profile
    ) {
      const data = {
        school: {
          value: eduHasAlready ? profile.profile.school?.value : edu?.value,
          ogrn: eduHasAlready ? profile.profile.school?.ogrn : edu?.data?.ogrn,
          city: eduHasAlready ? profile.profile.school?.city : eduCity,
        },
        school_group: group,
        external_profile: {
          name,
          surname: lastName,
          patronymic: patronymicName,
          gender: sex,
          birthdate: moment(birthday).format('YYYY-MM-DD'),
          address: {
            country: 'Россия',
            region: regionHasAlready
              ? profile.external_profile.address.region
              : region?.value,
            location: locationHasAlready
              ? profile.external_profile.address.location
              : location?.value,
          },
        },
      }
      dispatch(profileActions.saveProfile(data))
    } else {
      setIsValidate(true)
      if (moment(birthday) > moment()) {
        notification('error', '', 'Дата рождения не может быть больше текущей')
        setBirthdayError(true)
      }
    }
  }

  function formatSelected(suggestion: { value: string }): string {
    return dadataDots(suggestion.value)
  }

  const modalStyles = {
    overlay: {
      background: 'rgba(0, 0, 0, 0.4)',
    },
    modal: {
      width: 665,
      borderRadius: 50,
      padding: 0,
      margin: '2em',
      boxShadow: 'unset',
      overflow: 'unset',
    },
  }

  if (!profile) return null

  return (
    <Modal
      open={modal}
      showCloseIcon={false}
      onClose={onClose}
      styles={modalStyles}
    >
      <div className={'questionary'}>
        <div className={'questionary__title'}>Заполни Анкету</div>
        <div className={'questionary__profile'}>
          <div className={'personal-edit-block__column'}>
            <div className={'personal-edit-block__column-field'}>
              <div className={'label'}>Фамилия:</div>
              <input
                value={lastName}
                style={
                  isValidate && !lastName.length
                    ? { background: '#FCE5DF' }
                    : {}
                }
                onChange={onChangeLastName}
              />
            </div>
            <div className={'personal-edit-block__column-field'}>
              <div className={'label'}>Имя:</div>
              <input
                value={name}
                style={
                  isValidate && !name.length ? { background: '#FCE5DF' } : {}
                }
                onChange={onChangeName}
              />
            </div>
            <div className={'personal-edit-block__column-field'}>
              <div className={'label'}>Отчество:</div>
              <input value={patronymicName} onChange={onChangePatronymic} />
            </div>
            <div className={'personal-edit-block__column-additional'}>
              <div className={'sex'}>
                <div className={'label'}>Пол:</div>
                <div className={'sex__outer'}>
                  <div
                    onClick={() => setOpenSex(!openSex)}
                    className={openSex ? 'sex__select active' : 'sex__select'}
                    style={
                      !sex && openSex
                        ? { height: 134 }
                        : isValidate && !sex.includes('male')
                        ? { background: '#FCE5DF' }
                        : {}
                    }
                  >
                    <div
                      className={openSex ? 'value active' : 'value'}
                      style={
                        isValidate && !sex.includes('male')
                          ? { background: '#FCE5DF', border: '1px solid red' }
                          : {}
                      }
                    >
                      {sex ? (sex === 'male' ? 'М' : 'Ж') : '?'}
                    </div>
                    {sex ? (
                      openSex ? (
                        <div
                          className={'option'}
                          onClick={() => {
                            if (sex === 'male') {
                              setSex('female')
                            } else {
                              setSex('male')
                            }
                          }}
                        >
                          {sex === 'male' ? 'Ж' : 'М'}
                        </div>
                      ) : null
                    ) : openSex ? (
                      <>
                        <div
                          className={'option'}
                          onClick={() => setSex('female')}
                        >
                          Ж
                        </div>
                        <div
                          className={'option'}
                          onClick={() => setSex('male')}
                        >
                          М
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
              <div
                className={
                  isValidate && (!birthday || birthdayError)
                    ? 'birthday error'
                    : 'birthday'
                }
              >
                <div className={'label'}>
                  Дата
                  <br />
                  рождения:
                </div>
                <div
                  className={
                    isOpenCalendar ? 'birthday__date open' : 'birthday__date'
                  }
                >
                  <DatePicker
                    selected={birthday}
                    onChange={(date: Date) => {
                      setBirthday(date)
                      setBirthdayError(false)
                    }}
                    locale={'ru'}
                    dateFormat={'P'}
                    onCalendarOpen={() => setIsOpenCalendar(true)}
                    onCalendarClose={() => {
                      if (isStudent) {
                        if (moment().diff(birthday, 'years') >= 20) {
                          setModalChangeRole({ modal: true, type: 'teacher' })
                        }
                      }
                      if (isTeacher) {
                        if (moment().diff(birthday, 'years') < 20) {
                          setModalChangeRole({ modal: true, type: 'student' })
                        }
                      }
                      setIsOpenCalendar(false)
                    }}
                    popperPlacement={'bottom'}
                    popperProps={{
                      placement: 'bottom',
                      modifiers: [
                        {
                          name: 'flip',
                          options: {
                            enabled: false,
                            fallbackPlacements: ['bottom'],
                          },
                        },
                      ],
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={'questionary__edu'}>
          <div className={'education-edit-block__column'}>
            <div className={'education-edit-block__column-field'}>
              <div className={'label'} style={{ marginTop: 10 }}>
                Образовательное
                <br />
                учреждение:
              </div>
              <div className={edu ? 'input' : 'input clear'}>
                <PartySuggestions
                  token={REACT_APP_DADATA_KEY}
                  value={edu}
                  onChange={value => {
                    if (eduHasAlready) {
                      setEduHasAlready(false)
                    }
                    setEdu(value)
                    setTimeout(() => autosize.update($('.react-dadata__input')))
                  }}
                  okved={[
                    '85.13',
                    '85.14',
                    '85.21',
                    '85.12',
                    '85.4',
                    '85.41',
                    '85.42',
                  ]}
                  autoload={true}
                  inputProps={{
                    style:
                      isValidate && !edu && !eduHasAlready
                        ? { border: '1px solid red' }
                        : {},
                    onChange: () => {
                      setEduHasAlready(false)
                      setEdu(undefined)
                    },
                  }}
                  ref={suggestionsRef}
                  customInput={'textarea'}
                  count={30}
                  renderOption={suggestion => (
                    <div className={'suggestion'}>
                      <div className={'suggestion__name'}>
                        {suggestion.value}
                      </div>
                      <div className={'suggestion__address'}>
                        {suggestion.data?.address.value}
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>
            <div className={'education-edit-block__column-field center'}>
              <div className={'label'}>Класс/группа:</div>
              <div className={'input'}>
                <input
                  style={
                    isValidate && !group ? { border: '1px solid red' } : {}
                  }
                  value={group}
                  onChange={onChangeGroup}
                  className={'input-class'}
                  maxLength={256}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={'questionary__location'}>
          <div className={'about-edit-block__column-item'}>
            <div className={'label'}>Регион:</div>
            <div className={'input'}>
              <AddressSuggestions
                token={REACT_APP_DADATA_KEY}
                value={region}
                onChange={value => {
                  if (regionHasAlready) {
                    setRegionHasAlready(false)
                  }
                  value!.value = formatSelected({
                    value: value!.value,
                  })
                  setRegion(value)
                }}
                inputProps={{
                  style:
                    isValidate && !region && !regionHasAlready
                      ? { border: '1px solid red' }
                      : {},
                  onChange: () => setRegionHasAlready(false),
                }}
                ref={suggestionsRegionRef}
                filterFromBound={'region'}
                filterToBound={'region'}
                renderOption={value => formatSelected(value)}
              />
            </div>
          </div>
          <div className={'about-edit-block__column-item'}>
            <div className={'label'}>
              Населенный
              <br />
              пункт:
            </div>
            <div className={'input'}>
              <AddressSuggestions
                token={REACT_APP_DADATA_KEY}
                value={location}
                ref={suggestionsLocationRef}
                onChange={value => {
                  if (locationHasAlready) {
                    setLocationHasAlready(false)
                  }
                  value!.value = formatSelected({
                    value:
                      value!.data.settlement_with_type ||
                      value!.data.city_with_type ||
                      '',
                  })
                  setLocation(value)
                  const region = value?.data.region_with_type
                  if (region) {
                    const regionValue = Object.assign({}, value)
                    regionValue!.value = dadataDots(region)
                    setRegion(regionValue)
                  }
                }}
                inputProps={{
                  style:
                    isValidate && !location && !locationHasAlready
                      ? { border: '1px solid red' }
                      : {},
                  onChange: () => setLocationHasAlready(false),
                }}
                filterFromBound={'city'}
                filterToBound={'settlement'}
                renderOption={value => formatSelected(value)}
              />
            </div>
          </div>
        </div>
        <button
          disabled={isProcessSave}
          className={'questionary__button'}
          onClick={saveProfile}
        >
          {isProcessSave ? (
            <Loading
              style={{ margin: '0 0 5px' }}
              width={30}
              height={30}
              color={'#219321'}
            />
          ) : (
            'Готово'
          )}
        </button>
      </div>
      <ModalChangeRole
        modal={modalChangeRole.modal}
        type={modalChangeRole.type}
        isLoading={isProcessChangeProfileRole}
        onClose={() => setModalChangeRole(initialModalChangeRole)}
        onChangeProfileRole={() =>
          dispatch(profileActions.changeProfileRole(modalChangeRole.type))
        }
      />
    </Modal>
  )
}
